import { motion } from "framer-motion";
import { Reveal, ease } from "./Reveal";
import { LoopCarousel } from "./LoopCarousel";
import { CtaButton } from "./CtaButton";
import { PLATFORMS } from "../../data/platforms";

const Stat = ({ value, label }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
    <div className="font-display text-3xl leading-none text-[var(--cream)]">{value}</div>
    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#f5f1e6]/65">{label}</div>
  </div>
);

const Screen = ({ src, alt, glow, testId }) => (
  <div className="[perspective:1600px]">
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, rotateY: -14, rotateX: 6, scale: 0.94 }}
      whileInView={{ opacity: 1, rotateY: 0, rotateX: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.4, ease }}
      className="relative"
    >
      <div className="absolute -inset-6 rounded-[40px] opacity-60 blur-3xl" style={{ background: glow }} />
      <div className="relative overflow-hidden rounded-[22px] bg-black p-2 ring-1 ring-white/15 shadow-[0_50px_100px_-40px_rgba(0,0,0,0.9)]">
        <div className="mb-2 flex gap-1.5 px-2 pt-1">
          <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        </div>
        <img src={src} alt={alt} loading="lazy" className="aspect-[16/9] w-full rounded-[14px] object-cover" />
      </div>
    </motion.div>
  </div>
);

const PlatformBlock = ({ p, reverse }) => (
  <article data-testid={`platform-${p.id}`} className="relative">
    <div className={`mx-auto grid max-w-6xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-16 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <Reveal>
        <div className="flex items-center gap-4">
          <span className="font-display text-5xl leading-none" style={{ color: p.accent }}>{p.number}</span>
          <span className="h-px flex-1 bg-white/15" />
        </div>
        {p.logo ? (
          <img src={p.logo} alt={p.name} className="mt-6 h-16 w-auto object-contain md:h-20" />
        ) : null}
        <h3 className="font-display mt-5 text-3xl leading-tight text-[var(--cream)] md:text-5xl">{p.name}</h3>
        <p className="mt-4 text-base leading-relaxed text-[#f5f1e6]/80">{p.description}</p>
        <div className="mt-7 grid grid-cols-2 gap-3">
          {p.stats.map((s) => <Stat key={s.label} {...s} />)}
        </div>
        <div className="mt-7 flex flex-wrap gap-2">
          {p.features.map((f) => (
            <span key={f} className="rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 text-xs text-[#f5f1e6]/85">{f}</span>
          ))}
        </div>
      </Reveal>
      <Screen src={p.banner} alt={p.name} glow={p.glow} testId={`platform-${p.id}-screen`} />
    </div>

    {p.rows.map((r, i) => (
      <Reveal key={r.title} delay={0.1} className="mt-16">
        <div className="mx-auto mb-6 flex max-w-6xl items-end justify-between gap-4 px-5">
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.34em]" style={{ color: p.accent }}>{r.title}</h4>
          <span className="text-[11px] uppercase tracking-[0.2em] text-[#f5f1e6]/55">{r.images.length} conteúdos</span>
        </div>
        <LoopCarousel images={r.images} testId={`platform-${p.id}-carousel-${i}`} alt={`${p.name} — ${r.title}`} itemClass={r.itemClass} />
      </Reveal>
    ))}
  </article>
);

export const Platforms = () => (
  <section id="plataformas" data-testid="platforms-section" className="relative overflow-hidden bg-[var(--night)] py-24 md:py-32">
    <div className="grain absolute inset-0" />
    <div className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-[#d9b45a]/[0.07] blur-3xl" />
    <Reveal className="relative mx-auto max-w-3xl px-5 text-center">
      <p className="text-[11px] font-semibold uppercase tracking-[0.42em] text-[var(--gold)]">Por dentro das plataformas</p>
      <h2 data-testid="platforms-title" className="font-display mt-5 text-4xl leading-tight text-[var(--cream)] sm:text-5xl lg:text-6xl">
        Veja tudo o que <em>você vai receber</em>
      </h2>
      <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#f5f1e6]/80">
        Duas plataformas completas, com acesso vitalício, para estudar onde e quando quiser — no computador ou no celular.
      </p>
    </Reveal>
    <div className="relative mt-20 space-y-28 md:mt-24 md:space-y-36">
      {PLATFORMS.map((p, i) => <PlatformBlock key={p.id} p={p} reverse={i % 2 === 1} />)}
    </div>
    <Reveal className="relative mt-20 flex flex-col items-center gap-4 px-5 text-center">
      <p className="text-sm text-[#f5f1e6]/75">Tudo isso por <span className="text-[var(--gold)]">12x de R$ 46,44</span> na oferta de Black Friday</p>
      <CtaButton testId="platforms-cta-vitalicio" label="Quero as duas plataformas" />
    </Reveal>
  </section>
);
